import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, filter, map, take, tap } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, takeWhile } from 'rxjs/operators';

import {
  addCompany,
  addCompanySuccess,
  addCompanyWithOwner,
  addCompanyWithOwnerFailureUserExist,
  addCompanyWithOwnerSuccess,
} from '@companies/store/companies.actions';
import { selectAddCompanyWithOwnerLoading } from '@companies/store/companies.selectors';
import { AdministrationAbstract } from '@main-application/administration/abstract/administration-abstract';
import { VENDOR_ROLE_NAME } from '@main-application/administration/constants/vendor-role-name.constant';
import { combineUserWithProperties } from '@main-application/administration/functions/combineUserWithProperties';
import { formatPropertyStructureToTree } from '@main-application/administration/functions/propertyStructureToTree';
import {
  inviteNewUser,
  loadAllGeneralRoles,
} from '@main-application/administration/store/actions/administration.actions';
import {
  selectAllGeneralRoles,
  selectAllPropertiesStructure,
  selectAllUserToPropertyMappings,
} from '@main-application/administration/store/selectors/administration.selectors';
import { PhoneValidatorService } from '@main-application/shared/services/phone-validator.service';
import { selectNativeLanguagesEnumeration } from '@main-application/store/selectors/enumeration.selectors';
import { selectUserData } from '@main-application/store/selectors/user.selectors';
import { selectPortfolios } from '@portfolio/store/portfolio.selectors';
import { EnumerationItemFields } from '@shared/enums/enumeration-item-fields';
import { PermissionLevelType } from '@shared/enums/permission-level.enum';
import { PropertyType } from '@shared/enums/property-type.enum';
import { UserType, VendorEmployeeType } from '@shared/enums/user-type';
import { getEnumerationRadioListFunction } from '@shared/functions/get-enumeration-radio-list.function';
import { RestCompanyModel } from '@shared/interfaces/companies.interface';
import { PropertyBasicInfo } from '@shared/interfaces/propertyBasicInfo';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { TenantData } from '@shared/interfaces/tenant-data';
import { UserData } from '@shared/interfaces/user-data';
import { InviteRestUserModel } from '@shared/interfaces/user.interface';
import { PendingService } from '@shared/services/pending.service';
import { filterNullish$ } from '@shared/utils/rxjs/filter-nullish.rxjs.util';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { EmailPatternValidator, correctEmailInput } from '@ui-components/validators/email.validator';
import { RequiredOneOfValidator } from '@ui-components/validators/required-one-of.validator';

enum NewVendorFields {
  FirstName = 'firstName',
  LastName = 'lastName',
  Email = 'email',
  Company = 'company',
  PhoneNumber = 'phoneNumber',
  RoleId = 'roleId',
  UserGroupType = 'userGroupType',
  PhoneNumberAddedAsIdentity = 'phoneNumberAddedAsIdentity',
  EmailAddedAsIdentity = 'emailAddedAsIdentity',
  nativeLanguage = 'nativeLanguage',
}

interface NewVendor {
  [NewVendorFields.FirstName]: string;
  [NewVendorFields.LastName]: string;
  [NewVendorFields.Email]: string;
  [NewVendorFields.Company]: string;
  [NewVendorFields.PhoneNumber]: string;
  [NewVendorFields.RoleId]: number;
  [NewVendorFields.nativeLanguage]: number;
}

export interface AddVendorModalData {
  addOnlyVendor?: boolean;
  onlyAddBtn?: boolean;
  withPortfolioPropertiesSelector?: boolean;
  tenantData?: TenantData;
}

@UntilDestroy()
@Component({
  selector: 'app-add-vendor-modal',
  templateUrl: './add-vendor-modal.component.html',
  styleUrls: ['./add-vendor-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class AddVendorModalComponent extends AdministrationAbstract implements OnInit {
  form: FormGroup = this.initFormGroup();
  vendorRoleId: number;
  inProgress$ = this.store.select(selectAddCompanyWithOwnerLoading);
  formSubmitted = false;
  warningMessage = '';
  arePropertiesChanged = false;
  selectedProperties: PropertyBasicInfo[] = [];
  allProperties$ = combineLatest([
    this.store.select(selectAllPropertiesStructure).pipe(filterNullish$()),
    this.store.select(selectPortfolios),
  ]).pipe(
    map(([allProperties, portfolios]) => {
      const portfolioIds = new Set(portfolios.map(el => el.id));
      const filteredProperties = allProperties.filter(
        el => el.propertyType !== PropertyType.Property || portfolioIds.has(el.parentId)
      );
      return formatPropertyStructureToTree(filteredProperties);
    })
  );
  propertiesUserHasAccessTo$ = combineLatest([
    this.store.select(selectAllPropertiesStructure),
    this.store.select(selectUserData),
    this.store.select(selectAllUserToPropertyMappings),
  ]).pipe(
    filter(
      ([allProperties, userData, userToPropertyMapping]) => !!allProperties && !!userData && !!userToPropertyMapping
    ),
    map(([allProperties, userData, userToPropertyMapping]) =>
      combineUserWithProperties(userData.id, allProperties, userToPropertyMapping)
    )
  );
  isLoading = true;
  phoneNumberInvalid = false;
  VendorEmployeeType = VendorEmployeeType;
  vendorEmployeeToggleOptions = [
    {
      value: this.VendorEmployeeType.VendorAndEmployee,
      label: 'Vendor & Employee ',
      permission: PermissionLevelType.Management_Users,
    },
    {
      value: this.VendorEmployeeType.Vendor,
      label: 'Vendor',
      permission: PermissionLevelType.Management_Users,
    },
  ];
  federatedIssuerDomains: string[];
  domainExist$ = new BehaviorSubject<boolean>(false);
  emailAutoCheck = false;
  private isManualCheckboxClick = false;
  private emailAutoCheckSetup = false;
  nativeLanguages: IRadioButtonOption<number>[] = [];

  constructor(
    public fb: FormBuilder,
    public dialogRef: MatDialogRef<AddVendorModalComponent, RestCompanyModel>,
    store: Store<{}>,
    private actions$: Actions,
    private snackbarService: SnackbarService,
    @Inject(MAT_DIALOG_DATA) public data: AddVendorModalData,
    cdr: ChangeDetectorRef,
    private pendingService: PendingService,
    private phoneValidatorService: PhoneValidatorService
  ) {
    super(cdr, store);
  }

  get firstName(): FormControl<string> {
    return this.form.get(NewVendorFields.FirstName) as FormControl;
  }

  get lastName(): FormControl {
    return this.form.get(NewVendorFields.LastName) as FormControl;
  }

  get email(): FormControl {
    return this.form.get(NewVendorFields.Email) as FormControl;
  }

  get company(): FormControl {
    return this.form.get(NewVendorFields.Company) as FormControl;
  }

  get phoneNumber(): FormControl {
    return this.form.get(NewVendorFields.PhoneNumber) as FormControl;
  }

  get userGroupType(): FormControl<VendorEmployeeType> {
    return this.form.get(NewVendorFields.UserGroupType) as FormControl<VendorEmployeeType>;
  }

  get phoneNumberAddedAsIdentity(): FormControl {
    return this.form.get(NewVendorFields.PhoneNumberAddedAsIdentity) as FormControl;
  }

  get emailAddedAsIdentity(): FormControl {
    return this.form.get(NewVendorFields.EmailAddedAsIdentity) as FormControl;
  }

  get nativeLanguage(): FormControl {
    return this.form.get(NewVendorFields.nativeLanguage) as FormControl<number>;
  }

  ngOnInit(): void {
    this.store.dispatch(loadAllGeneralRoles());
    this.onPendingServiceChanges();
    this.onSelectUserData();
    this.initForm();
    this.initVendorRoleId();
    this.initNotifications();
    this.onEmailControlChange();

    this.userGroupType.valueChanges
      .subscribe(d => {
        this.userGroupType.value === this.VendorEmployeeType.VendorAndEmployee
          ? this.addEmployeeControls()
          : this.removeEmployeeControls();
        if (this.userGroupType.value === this.VendorEmployeeType.VendorAndEmployee) {
          this.onEmailValueChangeForAutoCheck();
        }
      })
      .untilDestroyed(this);

    if (this.userGroupType.value === this.VendorEmployeeType.VendorAndEmployee) {
      this.onEmailValueChangeForAutoCheck();
    }

    this.store
      .select(selectNativeLanguagesEnumeration)
      .pipe(
        untilDestroyed(this),
        tap(values => {
          this.nativeLanguages = getEnumerationRadioListFunction(
            values,
            null,
            false,
            EnumerationItemFields.enumerationValue,
            true
          );
          this.cdr.detectChanges();
        })
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private onEmailControlChange(): void {
    this.email?.valueChanges
      .pipe(
        distinctUntilChanged(),
        filter(() => !!this.data?.tenantData?.federatedIssuer),
        debounceTime(500)
      )
      .subscribe(value => this.checkForDomain(value))
      .untilDestroyed(this);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  send(): void {
    this.formSubmitted = true;
    if (
      this.form.invalid ||
      (this.userGroupType.value === this.VendorEmployeeType.VendorAndEmployee &&
        (this.phoneNumberInvalid || (!this.phoneNumber.value && this.phoneNumberAddedAsIdentity.value))) ||
      (this.userGroupType.value === this.VendorEmployeeType.VendorAndEmployee &&
        !this.email.value &&
        this.emailAddedAsIdentity.value)
    ) {
      this.form.markAllAsTouched();
      this.cdr.detectChanges();
      return;
    }

    if (
      this.userGroupType.value === this.VendorEmployeeType.VendorAndEmployee &&
      !this.phoneNumberAddedAsIdentity.value &&
      !this.emailAddedAsIdentity.value
    ) {
      this.snackbarService.error('Please check Register Email or Register Phone number as identity');
      return;
    }

    const value: NewVendor = this.form.value;
    const inviteUser: InviteRestUserModel = createInviteUser(value);
    const companyModel: RestCompanyModel = {
      displayName: value.company,
      id: 0,
      employees: [],
      addresses: [],
    };

    if (this.userGroupType.value === this.VendorEmployeeType.Vendor) {
      this.addCompany(companyModel);
      return;
    }

    this.addCompanyWithOwner(inviteUser, companyModel);
  }

  private initForm(): void {
    if (this.data.tenantData?.federatedIssuer && this.data.tenantData?.federatedIssuerDomains)
      this.federatedIssuerDomains = this.data.tenantData.federatedIssuerDomains.split(',');

    const userGroupTypeValue = this.data.addOnlyVendor
      ? this.VendorEmployeeType.Vendor
      : this.VendorEmployeeType.VendorAndEmployee;

    this.form.addControl(NewVendorFields.UserGroupType, new FormControl(userGroupTypeValue));

    if (this.userGroupType.value === this.VendorEmployeeType.VendorAndEmployee) this.addEmployeeControls();
  }

  private initVendorRoleId(): void {
    this.store
      .select(selectAllGeneralRoles)
      .pipe(untilDestroyed(this), filter(Boolean))
      .subscribe(allRoles => (this.vendorRoleId = allRoles.find(e => e.roleName == VENDOR_ROLE_NAME)?.id))
      .untilDestroyed(this);
  }

  private addCompany(companyModel: RestCompanyModel): void {
    this.store.dispatch(addCompany({ companyModel }));
    this.actions$
      .pipe(
        ofType(addCompanySuccess),
        take(1),
        tap(action => {
          this.snackbarService.success(`${action.companyModel.displayName} added`);
          this.dialogRef.close(action.companyModel);
        }),
        untilDestroyed(this)
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private addCompanyWithOwner(inviteUser: InviteRestUserModel, companyModel: RestCompanyModel): void {
    let selectedProperty: number[] = [];
    selectedProperty = this.selectedProperties.map(item => item.id);

    this.store.dispatch(
      addCompanyWithOwner({
        user: inviteUser,
        roleId: this.vendorRoleId,
        companyModel,
        selectedProperty,
      })
    );

    this.actions$
      .pipe(
        ofType(addCompanyWithOwnerSuccess),
        take(1),
        tap(action => {
          this.snackbarService.success(`${action.company.displayName} added`);
          this.dialogRef.close(action.company);
        }),
        untilDestroyed(this)
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private onInviteUser(inviteUser: InviteRestUserModel): void {
    const selectedProperty =
      this.arePropertiesChanged && this.selectedProperties ? this.selectedProperties.map(item => item.id) : [];

    this.store.dispatch(inviteNewUser({ user: inviteUser, roleId: this.vendorRoleId, selectedProperty }));
  }

  private initNotifications(): void {
    this.initCompanySuccess();
    this.initUserExistError();
  }

  private initFormGroup(): FormGroup {
    return this.fb.group({
      [NewVendorFields.Company]: [null as string, Validators.required],
    });
  }

  private initUserExistError(): void {
    this.actions$
      .pipe(
        ofType(addCompanyWithOwnerFailureUserExist),
        tap(data => {
          data?.phoneNumberAddedAsIdentity && data.phoneNumber
            ? this.snackbarService.error(`User already exists with phone ${data.phoneNumber}`)
            : this.snackbarService.error(`User already exists with email ${data.existedUserEmail}`);
        }),
        untilDestroyed(this)
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private initCompanySuccess(): void {
    this.actions$
      .pipe(
        ofType(addCompanyWithOwnerSuccess),
        tap(action => {
          this.snackbarService.success(`${action.company.displayName} added`);
          this.dialogRef.close(action.company);
        }),
        untilDestroyed(this)
      )
      .subscribe()
      .untilDestroyed(this);
  }

  selectedRootsChange(selectedRoots: PropertyBasicInfo[]): void {
    this.arePropertiesChanged = true;
    this.selectedProperties = selectedRoots;
  }

  private onSelectUserData(): void {
    this.store
      .select(selectUserData)
      .pipe(
        untilDestroyed(this),
        filter((userData: UserData) => !!userData),
        tap((userData: UserData) => {
          this.userData = userData;
        })
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private addEmployeeControls(): void {
    this.form.addControl(NewVendorFields.FirstName, new FormControl(null as string, Validators.required));
    this.form.addControl(NewVendorFields.LastName, new FormControl(null as string, Validators.required));
    this.form.addControl(NewVendorFields.nativeLanguage, new FormControl(0 as number, Validators.required));
    this.form.addControl(
      NewVendorFields.Email,
      new FormControl(null as string, [EmailPatternValidator(true), Validators.email])
    );
    this.form.addControl(
      NewVendorFields.PhoneNumber,
      new FormControl(null as string, [this.phoneValidatorService.phoneNumberValidator()])
    );
    this.form.addControl(NewVendorFields.PhoneNumberAddedAsIdentity, new FormControl(false));
    this.form.addControl(NewVendorFields.EmailAddedAsIdentity, new FormControl(false));
    this.form.setValidators(RequiredOneOfValidator([NewVendorFields.Email, NewVendorFields.PhoneNumber]));

    correctEmailInput(this.email);
    this.form.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  private removeEmployeeControls(): void {
    this.form.removeControl(NewVendorFields.FirstName);
    this.form.removeControl(NewVendorFields.nativeLanguage);
    this.form.removeControl(NewVendorFields.LastName);
    this.form.removeControl(NewVendorFields.Email);
    this.form.removeControl(NewVendorFields.PhoneNumber);
    this.form.removeControl(NewVendorFields.PhoneNumberAddedAsIdentity);
    this.form.removeControl(NewVendorFields.EmailAddedAsIdentity);

    this.emailAutoCheckSetup = false;

    const newValidator = RequiredOneOfValidator([], true);
    this.form.setValidators([newValidator]);
    this.form.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  private onPendingServiceChanges(): void {
    this.pendingService.pending$
      .pipe(
        takeWhile(data => data !== false, true),
        delay(300)
      )
      .subscribe(data => {
        if (data === false) {
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      })
      .untilDestroyed(this);
  }

  private onEmailValueChangeForAutoCheck(): void {
    if (this.emailAutoCheckSetup || !this.form.get(NewVendorFields.Email)) {
      return;
    }

    this.emailAutoCheckSetup = true;

    this.email?.valueChanges
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe(value => {
        if (
          !this.domainExist$.getValue() &&
          !this.emailAddedAsIdentity.value &&
          !this.emailAutoCheck &&
          !this.isManualCheckboxClick &&
          value &&
          value.trim().length > 0
        ) {
          this.emailAddedAsIdentity.setValue(true);
          this.emailAutoCheck = true;
        }
      })
      .untilDestroyed(this);

    this.emailAddedAsIdentity.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isManualCheckboxClick = true;
        setTimeout(() => {
          this.isManualCheckboxClick = false;
        });
      })
      .untilDestroyed(this);
  }

  private checkForDomain(value: string): void {
    const domain = value.match(/@(.+)$/)?.[1] || null;
    domain && this.federatedIssuerDomains.includes(domain?.trim())
      ? this.domainExist$.next(true)
      : this.domainExist$.next(false);
  }
}

function createInviteUser(formData: NewVendor): InviteRestUserModel {
  return {
    email: formData[NewVendorFields.Email],
    firstName: formData[NewVendorFields.FirstName],
    lastName: formData[NewVendorFields.LastName],
    userGroupType: UserType.Contractor,
    displayName: `${formData.firstName} ${formData.lastName}`,
    phoneNumber: formData[NewVendorFields.PhoneNumber],
    phoneNumberAddedAsIdentity: formData[NewVendorFields.PhoneNumberAddedAsIdentity],
    emailAddedAsIdentity: formData[NewVendorFields.EmailAddedAsIdentity],
    nativeLanguage: formData[NewVendorFields.nativeLanguage],
  };
}
